import { assign, get, isEmpty, pick } from 'lodash';
import type { Client } from 'urql';

import { calcTrialDay } from '@revelio/core';
import { USER_DETAILS_QUERY } from '@revelio/data-access';

interface GetUserTrackingData {
  userId: string;
  creationDate: string;
  gqlClient: Client;
}

export const getUserTrackingData = async ({
  userId,
  creationDate,
  gqlClient,
}: GetUserTrackingData) => {
  const { data } = await gqlClient
    .query(USER_DETAILS_QUERY, { ids: [userId] })
    .toPromise();

  if (!data) {
    return undefined;
  }

  const loggedInUser = get(data, 'users[0]', {});

  if (isEmpty(loggedInUser)) {
    return undefined;
  }

  const user = pick(loggedInUser, [
    'username',
    'email',
    'client_name',
    'id',
    'live',
  ]);

  const isTrialUser = !user.live;

  // Stamped by user-mgmt when the "tell us about yourself" step is submitted;
  // empty means the user bailed before personalizing, and the login flow
  // routes them to /personalize instead of the dashboard.
  const personalizationCompletedAt =
    (get(loggedInUser, 'metadata') ?? []).find(
      (entry: { key?: unknown; value?: string | null } | null) =>
        String(entry?.key) === 'personalization_completed'
    )?.value || null;

  const userToSet = { ...user, trial: isTrialUser, personalizationCompletedAt };

  if (creationDate && isTrialUser) {
    const trialDay = calcTrialDay(creationDate);

    assign(userToSet, { trial_day: trialDay });
  }

  return userToSet;
};
